import React, { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router';
import { Form } from 'components/atoms/form';
import { RoutePaths } from 'constant';
import { RootState } from 'data';
import { setDirtyState } from 'data/templates';
import { TemplateState } from 'data/templates/api';
import {
  ProjectTemplateApi,
  useActiveProjectTemplateStore,
  useActiveTemplateId,
} from 'data/templates/projects';
import { saveProjectTemplate } from 'data/templates/projects/actions';
import {
  AutomationByReferenceId,
  useGetAutomation,
} from 'hooks/use-automations';
import {
  RefType,
  useAddKeyEventAutomation,
  useGetCaptureExpectedEventAutomationForRefId,
  useUpdateExpectedEventAutomationForRefId,
  useUpdateKeyEventAutomation,
} from 'hooks/use-key-events';
import { queryClient, getTemplatesQueryKey } from 'queries';
import { refToAutomationSource } from 'utils/automations';
import { queueMacrotask } from 'utils/macrotask';
import { ANY_STATUS_KEY } from './constant';
import { KeyEventForm } from './key-event-form';
import { KeyEventDrawer } from './key-events-drawer';

export const KeyEventPage: React.FC = () => {
  const { refId, refType, automationId, templateId } =
    useParams<{
      refType: string;
      refId: string;
      automationId: string;
      templateId: string;
    }>();
  const [form] = Form.useForm();
  const { data: automation } = useGetAutomation({
    automationId: Number(automationId),
  });

  const captureExpectedDateAutomation =
    useGetCaptureExpectedEventAutomationForRefId(
      Number(templateId),
      refType,
      Number(refId),
      automation?.actionsV2?.at(0)?.input?.fieldId,
    );

  const updateExpectedDateAutomation = useUpdateExpectedEventAutomationForRefId(
    Number(templateId),
    refType,
    Number(refId),
    automation?.actionsV2?.at(0)?.input?.fieldId,
  );

  const [isLoading, setLoading] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const fieldsStore = useSelector((state: RootState) => state.fields.byId);
  const { mutateAsync: addAutomation } = useAddKeyEventAutomation();
  const { mutateAsync: editAutomation } = useUpdateKeyEventAutomation();
  const qc = useQueryClient();
  const dispatch = useDispatch();
  const activeTemplate = useActiveProjectTemplateStore();
  const [visible, _] = useState(true);

  const refName = useMemo(
    () =>
      refType === 'phase'
        ? activeTemplate.projectPhases?.[Number(refId)]?.projectPhaseName
        : activeTemplate.tasks?.[Number(refId)]?.taskName,
    [activeTemplate.projectPhases, activeTemplate.tasks, refId, refType],
  );

  const activeTemplateId = useActiveTemplateId();
  const onSave = async () => {
    await form.validateFields();

    setLoading(true);
    queueMacrotask(async () => {
      const values = form.getFieldsValue();
      const fieldId = form.getFieldValue('keyEventId').value;
      const field = fieldsStore[fieldId][1];

      if (automation !== undefined) {
        await editAutomation({
          templateId: Number(templateId),
          capturePlannedDate: values.captureExpectedEvent,
          field,
          refId: Number(refId),
          refType: refToAutomationSource(refType),
          toLabel: values.toLabel.value,
          fromLabel:
            !values.fromLabel?.value ||
            values.fromLabel?.value === ANY_STATUS_KEY
              ? undefined
              : values.fromLabel?.value,
          updatePlannedDate: values.updatePlannedDate,
          startOrEndDate: values.startOrDueDate,
        });
        setLoading(false);
        await qc.refetchQueries(
          AutomationByReferenceId(
            refToAutomationSource(refType),
            Number(refId),
          ),
        );
        const [goTo] = location.pathname.split('/automations');

        history.push(goTo);

        return;
      }

      const add = async (template: Pick<ProjectTemplateApi, 'templateId'>) => {
        if (template.templateId !== 'new') {
          if (field) {
            await addAutomation({
              templateId: template.templateId as number,
              capturePlannedDate: values.captureExpectedEvent,
              field,
              refId: Number(refId),
              refType: refToAutomationSource(refType),
              toLabel: values.toLabel.value,
              fromLabel:
                !values.fromLabel?.value ||
                values.fromLabel?.value === ANY_STATUS_KEY
                  ? undefined
                  : values.fromLabel?.value,
              updatePlannedDate: values.updatePlannedDate,
              startOrEndDate: values.startOrDueDate,
            });
            await qc.refetchQueries(
              AutomationByReferenceId(refType, Number(refId)),
            );
            if (template?.templateId) {
              history.push(
                RoutePaths.projectTemplate.replaceAll(
                  ':templateId',
                  template?.templateId?.toString(),
                ),
              );
            }
          }
          setLoading(false);
        }
      };

      if (field !== undefined) {
        setLoading(true);
        dispatch(
          saveProjectTemplate({
            type: TemplateState.Published,
            async handleSuccess(template) {
              dispatch(
                setDirtyState({
                  id: activeTemplateId,
                  _state: false,
                  handleSuccess: async () => {
                    await add(template);
                  },
                }),
              );
              queryClient.invalidateQueries(getTemplatesQueryKey());
            },
          }),
        );
      }
    });
  };

  const onCancel = () => {
    const goTo = location.pathname.split('/automations');

    history.push(goTo[0]);
  };

  useEffect(() => {
    if (refType === 'phase') {
      const phase = activeTemplate.projectPhases?.[Number(refId)];

      if (!phase) {
        history.push(
          RoutePaths.projectTemplate.replaceAll(
            ':templateId',
            activeTemplateId.toString(),
          ),
        );
      }
    } else if (refType === 'task') {
      const task = activeTemplate.tasks?.[Number(refId)];

      if (!task) {
        history.push(
          RoutePaths.projectTemplate.replaceAll(
            ':templateId',
            activeTemplateId.toString(),
          ),
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <KeyEventDrawer
      refName={refName}
      onCancel={onCancel}
      onSave={onSave}
      visible={visible}
      onClose={onCancel}
      isSaving={isLoading}
    >
      <KeyEventForm
        templateId={Number(templateId)}
        captureExpectedDateAutomation={captureExpectedDateAutomation}
        updateExpectedDateAutomation={updateExpectedDateAutomation}
        form={form}
        automation={automation}
        refId={Number(refId)}
        refType={refType as RefType}
        refName={refName}
      />
    </KeyEventDrawer>
  );
};
